import { Stack, Theme, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { DynamicIcon, FormAutocompleteProps, FormLabel } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SidePanelRow } from 'src/components/SidePanelRow';
import { toSxArray } from 'src/utils/sx';

export type BooleanSelectMenuFormValue = { id: string; label: string };

export const BooleanSelectMenuField = ({
  field,
  disabled,
}: {
  field: FormAutocompleteProps;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const booleanOptions = useMemo(
    () => (field.options ?? []) as BooleanSelectMenuFormValue[],
    [field.options],
  );

  const selectMenuOptions = useMemo<SelectMenuOption[]>(
    () =>
      booleanOptions.map((option) => ({
        value: option.id,
        title: option.label,
        icon: (
          <DynamicIcon
            library="lucide"
            color={
              option.id === 'Yes'
                ? theme.palette.custom.OV.sys.success
                : theme.palette.custom.OV.sys.error
            }
            name={option.id === 'Yes' ? 'check-circle' : 'circle-x'}
          />
        ),
      })),
    [booleanOptions, theme],
  );

  return (
    <SidePanelRow key={field.label}>
      <FormLabel label={field.label} />
      <Stack direction="row" width="100%" gap={1}>
        <Controller
          name={field.name}
          rules={{ required: field.required }}
          render={({ field: controllerField }) => {
            const value = (controllerField.value ??
              field.defaultValue ??
              null) as BooleanSelectMenuFormValue | null;

            return (
              <SelectMenu
                label={field.label ?? 'Select option'}
                options={selectMenuOptions}
                value={value?.id ?? null}
                onChange={(newValue) => {
                  const selectedOption = booleanOptions.find(
                    (option) => option.id === newValue,
                  );

                  if (selectedOption) {
                    controllerField.onChange(selectedOption);
                  }
                }}
                disabled={Boolean(disabled || field.disabled)}
                variant="inline"
                plainOptionIcons
                sx={[
                  { width: '100%' },
                  ...toSxArray(field.sx),
                  (theme: Theme) => ({
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.utils.important('1rem'),
                    },
                  }),
                ]}
              />
            );
          }}
        />
      </Stack>
    </SidePanelRow>
  );
};
